import { Directive, ElementRef, OnChanges, Renderer2, SimpleChanges, inject, input } from '@angular/core';

/**
 * Local standalone port of the AngularJS `ftBackgroundImage` directive
 * (account-v1/.../components/common/images/backgroundImage.js) — the exact directive
 * the FROZEN baseline uses to paint the curation cards' photo / quote backdrop and
 * the profile-photo avatars. Reproduced faithfully: probe the URL with `new Image()`,
 * set an inline `background-image` on success, else `fallbackImage`, else add the
 * `fallbackClass` (the silhouette). Same algorithm + same (no-`!important`) inline
 * style as v1 → the rendered pixels match the baseline.
 *
 * Kept LOCAL + standalone (not the shared account-shared/images BackgroundImageComponent,
 * which would drag ImagesModule → UserProfileImageComponent + MatIcon) so the curation
 * card never touches the account-shared barrel — avoiding the documented eval-order
 * fragility ([[project_account_barrel_eval_order_fragility]]).
 */
@Directive({
    selector: '[ftBackgroundImage]',
    standalone: true,
})
export class CurationBackgroundImageDirective implements OnChanges {
    readonly ftBackgroundImage = input<string | null | undefined>();
    readonly fallbackImage = input<string | null | undefined>();
    readonly fallbackClass = input<string | null | undefined>();

    private hasCheckedImageExistance = false;
    private imageHasChanged = false;
    private imageUrl: string | null | undefined;

    private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly renderer = inject(Renderer2);

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['ftBackgroundImage']) return;
        // v1 $watch semantics: a CHANGED url re-runs the existence check (the contest
        // sheets rebind from the property default to the contest's own image after load;
        // a once-only guard would freeze the first value). Verified against the live v1
        // sequence: default paints, the changed-to URL 404s → inline `url()` (blank).
        const newImageUrl = this.ftBackgroundImage();
        this.imageHasChanged = newImageUrl != this.imageUrl;
        this.imageUrl = newImageUrl;
        if (newImageUrl || this.imageHasChanged) {
            this.setBackgroundImage();
        } else {
            this.setFallbackClass();
        }
    }

    private setBackgroundImage(): void {
        if (this.hasCheckedImageExistance && !this.imageHasChanged) return;
        this.hasCheckedImageExistance = true;
        this.checkImageExistance(this.imageUrl ?? '').then((exists) => {
            if (exists) {
                this.renderer.setStyle(this.el.nativeElement, 'background-image', `url(${this.imageUrl})`);
            } else if (this.fallbackImage()) {
                this.renderer.setStyle(this.el.nativeElement, 'background-image', `url(${this.fallbackImage()})`);
            } else {
                this.renderer.setStyle(this.el.nativeElement, 'background-image', 'url()');
                this.setFallbackClass();
            }
        });
    }

    private setFallbackClass(): void {
        const fallbackClass = this.fallbackClass();
        if (!fallbackClass) return;
        fallbackClass
            .split(' ')
            .filter((klass) => !!klass)
            .forEach((klass) => this.renderer.addClass(this.el.nativeElement, klass));
    }

    private checkImageExistance(url: string): Promise<boolean> {
        return new Promise((resolve) => {
            let image: HTMLImageElement | null = new Image();
            image.onload = () => {
                resolve(true);
                image = null;
            };
            image.onerror = () => {
                resolve(false);
                image = null;
            };
            image.src = url;
        });
    }
}
